import { Badge, Button, Code, Loader, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { IconFileText, IconPlayerPause, IconPlayerPlay, IconPlayerStop } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { TaskArgsView } from './TaskArgsView'
import { RunLogModal } from './RunLogModal'
import {
  formatDateTime,
  isLiveRunStatus,
  runDuration,
  FOLLOWUP_STATUS_COLORS,
  RUN_STATUS_COLORS,
  useTaskRun,
  type RunSelection,
  type TaskRunInfo,
} from './hooks'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <div className="text-sm break-words">{children}</div>
    </div>
  )
}

/** The annotated followup items a completed run returned, each with its outcome. */
function FollowupList({ run }: { run: TaskRunInfo }) {
  if (!run.followup || run.followup.length === 0) return <>—</>
  return (
    <div className="space-y-2">
      {run.followup.map((item, index) => (
        <div key={index} className="rounded border border-gray-200 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{item.slug}</span>
            <Badge color={FOLLOWUP_STATUS_COLORS[item.status]} variant="light" size="sm">
              {item.status}
            </Badge>
            {item.message && (
              <Text size="xs" c="red" className="min-w-0 truncate" title={item.message}>
                {item.message}
              </Text>
            )}
          </div>
          {item.args != null && (
            <div className="mt-1">
              <TaskArgsView slug={item.slug} value={item.args} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** Bottom-right panel: details of the selected run; keeps polling while the run is live. */
export function RunDetails({ selection }: { selection: RunSelection | null }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const { data, isLoading } = useTaskRun(selection)
  const run = data?.data
  const [logOpened, { open: openLog, close: closeLog }] = useDisclosure(false)

  // cancel: pending/paused → cancelled outright, started → cooperative 'cancel';
  // pause: pending → paused; resume: paused → pending. Once a run starts it can only
  // be cancelled.
  const runAction = useMutation(
    orpc.taskRunAction.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.getTaskRun.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.listTaskRuns.key() }),
        ])
      },
      onError: (error) =>
        showNotification({
          color: 'red',
          title: 'Run action failed',
          message: error instanceof Error ? error.message : 'Unknown error',
        }),
    }),
  )

  if (!selection) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text c="dimmed" size="sm">
          Select a run
        </Text>
      </div>
    )
  }
  if (isLoading || !run) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader size="sm" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-3">
      <div className="mb-3 flex items-center gap-2">
        <Text fw={600}>Run #{run.id}</Text>
        <Badge color={RUN_STATUS_COLORS[run.status]} variant="light">
          {run.status}
        </Badge>
        <div className="ml-auto flex gap-2">
          {run.status === 'pending' && (
            <Button
              size="compact-sm"
              variant="light"
              color="violet"
              leftSection={<IconPlayerPause size={14} />}
              loading={runAction.isPending}
              onClick={() => runAction.mutate({ slug: run.slug, id: run.id, action: 'pause' })}
            >
              Pause
            </Button>
          )}
          {run.status === 'paused' && (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconPlayerPlay size={14} />}
              loading={runAction.isPending}
              onClick={() => runAction.mutate({ slug: run.slug, id: run.id, action: 'resume' })}
            >
              Resume
            </Button>
          )}
          {(isLiveRunStatus(run.status) || run.status === 'paused') && (
            <Button
              size="compact-sm"
              variant="light"
              color="red"
              leftSection={<IconPlayerStop size={14} />}
              loading={runAction.isPending}
              // 'cancel' = already requested; the runner is winding down.
              disabled={run.status === 'cancel'}
              onClick={() => runAction.mutate({ slug: run.slug, id: run.id, action: 'cancel' })}
            >
              {run.status === 'cancel' ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconFileText size={14} />}
            // The log exists only once an executor has claimed the run.
            disabled={!run.startedAt}
            onClick={openLog}
          >
            View log
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Field label="Task">
          <span className="font-mono">{run.slug}</span>
        </Field>
        <Field label="Triggered by">{run.actor ?? 'scheduler'}</Field>
        <Field label="Created">{formatDateTime(run.createdAt)}</Field>
        <Field label="Run at">{formatDateTime(run.runAt)}</Field>
        <Field label="Started">{formatDateTime(run.startedAt)}</Field>
        <Field label="Finished">{formatDateTime(run.finishedAt)}</Field>
        <Field label="Duration">{runDuration(run) ?? '—'}</Field>
        <Field label="Last heartbeat">{formatDateTime(run.heartbeatAt)}</Field>
        <Field label="Followup action">{run.followupAction}</Field>
      </div>
      <div className="mt-3 space-y-3">
        <Field label="Args">
          <TaskArgsView slug={run.slug} value={run.args} />
        </Field>
        <Field label="Summary">
          {run.summary ? (
            <Code block className="whitespace-pre-wrap">
              {run.summary}
            </Code>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Followup">
          <FollowupList run={run} />
        </Field>
      </div>
      {run.startedAt && <RunLogModal run={run} opened={logOpened} onClose={closeLog} />}
    </div>
  )
}
